import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';

// Compassionate Communication Wheel - Interactive Circular
export function CommunicationWheel({ onSave, savedData }) {
  const [currentNeed, setCurrentNeed] = useState(savedData?.need || '');
  const [feelings, setFeelings] = useState(savedData?.feelings || '');
  const [strategies, setStrategies] = useState(savedData?.strategies || '');
  const [requests, setRequests] = useState(savedData?.requests || '');

  useEffect(() => {
    onSave({ need: currentNeed, feelings, strategies, requests });
  }, [currentNeed, feelings, strategies, requests]);

  return (
    <div className="space-y-8">
      <div className="p-6 rounded-sm" style={{ backgroundColor: 'hsl(138 25% 96%)', border: '2px solid hsl(138 30% 40%)' }}>
        <h3 className="text-2xl font-bold mb-3">Compassionate Communication Wheel</h3>
        <p className="text-base text-gray-700">
          A tool for identifying needs, feelings, and strategies/requests in conflict. Work from the center outward.
        </p>
      </div>

      {/* Centered Circular Layout */}
      <div className="relative w-full max-w-2xl mx-auto" style={{ height: '600px' }}>
        {/* Center Circle: NEED */}
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-20">
          <div className="w-56 h-56 rounded-full bg-black text-white flex items-center justify-center p-6 border-4 border-white shadow-2xl">
            <Textarea
              placeholder="Core NEED"
              value={currentNeed}
              onChange={(e) => setCurrentNeed(e.target.value)}
              className="bg-transparent text-white placeholder-gray-300 border-none text-center resize-none text-lg font-bold"
              rows={4}
            />
          </div>
        </div>

        {/* Outer Cards Positioned Around Center */}
        <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-64">
          <Card style={{ borderRadius: 0, border: '3px solid #ef4444' }}>
            <CardHeader style={{ backgroundColor: '#fee2e2' }}>
              <CardTitle className="text-lg">Feelings</CardTitle>
            </CardHeader>
            <CardContent className="pt-3">
              <Textarea
                placeholder="What feelings..."
                value={feelings}
                onChange={(e) => setFeelings(e.target.value)}
                className="min-h-[60px] text-sm border-black"
                style={{ borderRadius: 0 }}
              />
            </CardContent>
          </Card>
        </div>

        <div className="absolute bottom-0 left-0 w-64">
          <Card style={{ borderRadius: 0, border: '3px solid #3b82f6' }}>
            <CardHeader style={{ backgroundColor: '#dbeafe' }}>
              <CardTitle className="text-lg">Strategies</CardTitle>
            </CardHeader>
            <CardContent className="pt-3">
              <Textarea
                placeholder="What actions..."
                value={strategies}
                onChange={(e) => setStrategies(e.target.value)}
                className="min-h-[60px] text-sm border-black"
                style={{ borderRadius: 0 }}
              />
            </CardContent>
          </Card>
        </div>

        <div className="absolute bottom-0 right-0 w-64">
          <Card style={{ borderRadius: 0, border: '3px solid #10b981' }}>
            <CardHeader style={{ backgroundColor: '#d1fae5' }}>
              <CardTitle className="text-lg">Requests</CardTitle>
            </CardHeader>
            <CardContent className="pt-3">
              <Textarea
                placeholder="Would you be willing..."
                value={requests}
                onChange={(e) => setRequests(e.target.value)}
                className="min-h-[60px] text-sm border-black"
                style={{ borderRadius: 0 }}
              />
            </CardContent>
          </Card>
        </div>
      </div>

      <div className="p-6 bg-gray-50 border-l-4 border-black">
        <h4 className="font-bold mb-2">How to Use the Wheel:</h4>
        <ol className="space-y-2 text-sm list-decimal list-inside">
          <li>Start in the CENTER: What need is not being met?</li>
          <li>FEELINGS: What emotions arise from this unmet need?</li>
          <li>STRATEGIES: What specific actions could meet this need?</li>
          <li>REQUESTS: Turn strategies into clear, doable requests</li>
        </ol>
        
        {currentNeed && feelings && strategies && requests && (
          <div className="mt-6">
            <Button
              onClick={() => {
                const summary = `Communication Wheel Practice:\n\nNeed: ${currentNeed}\nFeelings: ${feelings}\nStrategies: ${strategies}\nRequests: ${requests}\n\nCompleted: ${new Date().toLocaleString()}`;
                
                // Save to journal via backend
                const userId = localStorage.getItem('workbook_user_id');
                if (userId) {
                  fetch(`${process.env.REACT_APP_BACKEND_URL}/api/workbook/journal`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      user_id: userId,
                      title: `Communication Wheel: ${currentNeed}`,
                      content: summary,
                      tags: ['communication_wheel', 'worksheet']
                    })
                  });
                }
                
                alert('✨ Communication Wheel practice saved to your dashboard journal!');
              }}
              className="bg-black text-white hover:bg-black/90 w-full py-4 text-lg"
              style={{ borderRadius: 0 }}
            >
              ✨ Save to My Journal
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

// Mutual Strategies Worksheet - Venn Diagram Style
export function MutualStrategies({ onSave, savedData }) {
  const [need, setNeed] = useState(savedData?.need || '');
  const [personAStrategies, setPersonAStrategies] = useState(savedData?.personAStrategies || []);
  const [personBStrategies, setPersonBStrategies] = useState(savedData?.personBStrategies || []);
  const [mutualStrategies, setMutualStrategies] = useState(savedData?.mutualStrategies || []);
  const [newStrategyA, setNewStrategyA] = useState('');
  const [newStrategyB, setNewStrategyB] = useState('');
  const [newMutual, setNewMutual] = useState('');

  useEffect(() => {
    onSave({ need, personAStrategies, personBStrategies, mutualStrategies });
  }, [need, personAStrategies, personBStrategies, mutualStrategies]);

  return (
    <div className="space-y-8">
      <div className="p-6 rounded-sm" style={{ backgroundColor: 'hsl(138 25% 96%)', border: '2px solid hsl(138 30% 40%)' }}>
        <h3 className="text-2xl font-bold mb-3">Finding Mutual Strategies (Page 26)</h3>
        <div className="text-base text-gray-700 space-y-3">
          <p className="font-semibold">
            In NVC, it is commonly understood that <strong>conflict is never between needs, but rather always between strategies.</strong>
          </p>
          <p>
            This worksheet is a tool for helping two people discover mutual strategies for getting needs met. 
            We are much more likely to get our needs met when we focus on what we want, instead of what we don't want.
          </p>
          <p className="text-sm italic">From Steve Torma's relational practice toolkit</p>
        </div>
      </div>

      {/* Instructions */}
      <div className="p-4 bg-gray-50 border-l-4 border-black">
        <h4 className="font-bold mb-2">How to Use:</h4>
        <ol className="space-y-2 text-sm list-decimal list-inside">
          <li><strong>Step 1:</strong> Choose a need you'd like to have met better in your relationship - write it in NEED(S) below</li>
          <li><strong>Step 2:</strong> Each person writes their suggested strategies in the outside circles</li>
          <li><strong>Step 3:</strong> Discuss all strategies and find mutually satisfying ones to try</li>
          <li>Be creative - look for strategies related to but not identical to the originals</li>
          <li>This works for specific needs OR just enjoying your relationship more</li>
        </ol>
      </div>

      {/* Step 1: Choose Need */}
      <Card style={{ borderRadius: 0, border: '2px solid black' }}>
        <CardHeader>
          <CardTitle>Step 1: Choose a Need</CardTitle>
        </CardHeader>
        <CardContent>
          <Label className="mb-2 block">What need would you like to have met better in this relationship?</Label>
          <Input
            placeholder="e.g., Connection, Understanding, Support, Autonomy..."
            value={need}
            onChange={(e) => setNeed(e.target.value)}
            className="text-xl py-6 border-black"
            style={{ borderRadius: 0 }}
          />
        </CardContent>
      </Card>

      {/* Venn Diagram - 3 Circles */}
      <div className="grid md:grid-cols-3 gap-6">
        {/* Person A Strategies */}
        <Card style={{ borderRadius: 0, border: '3px solid #3b82f6' }}>
          <CardHeader style={{ backgroundColor: '#dbeafe' }}>
            <CardTitle>Person A's Ideas</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex gap-2">
              <Input
                placeholder="Add strategy..."
                value={newStrategyA}
                onChange={(e) => setNewStrategyA(e.target.value)}
                className="border-black"
                style={{ borderRadius: 0 }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newStrategyA) {
                    setPersonAStrategies([...personAStrategies, newStrategyA]);
                    setNewStrategyA('');
                  }
                }}
              />
              <Button 
                onClick={() => {
                  if (newStrategyA) {
                    setPersonAStrategies([...personAStrategies, newStrategyA]);
                    setNewStrategyA('');
                  }
                }}
                className="bg-black text-white"
                style={{ borderRadius: 0 }}
              >
                +
              </Button>
            </div>
            <div className="space-y-2">
              {personAStrategies.map((strategy, i) => (
                <div key={i} className="flex justify-between items-center p-2 bg-white border border-gray-300">
                  <span className="text-sm">{strategy}</span>
                  <button 
                    onClick={() => setPersonAStrategies(personAStrategies.filter((_, idx) => idx !== i))}
                    className="text-red-600 hover:text-red-800"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Mutual Strategies (Overlap) */}
        <Card style={{ borderRadius: 0, border: '3px solid #10b981' }}>
          <CardHeader style={{ backgroundColor: '#d1fae5' }}>
            <CardTitle>Mutual Strategies ✨</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-gray-600">Strategies that work for BOTH of you:</p>
            <div className="flex gap-2">
              <Input
                placeholder="Mutual strategy..."
                value={newMutual}
                onChange={(e) => setNewMutual(e.target.value)}
                className="border-black"
                style={{ borderRadius: 0 }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newMutual) {
                    setMutualStrategies([...mutualStrategies, newMutual]);
                    setNewMutual('');
                  }
                }}
              />
              <Button 
                onClick={() => {
                  if (newMutual) {
                    setMutualStrategies([...mutualStrategies, newMutual]);
                    setNewMutual('');
                  }
                }}
                className="bg-black text-white"
                style={{ borderRadius: 0 }}
              >
                +
              </Button>
            </div>
            <div className="space-y-2">
              {mutualStrategies.map((strategy, i) => (
                <div key={i} className="p-3 bg-green-50 border-2 border-green-600 font-semibold">
                  <div className="flex justify-between items-center">
                    <span className="text-sm">{strategy}</span>
                    <button 
                      onClick={() => setMutualStrategies(mutualStrategies.filter((_, idx) => idx !== i))}
                      className="text-red-600"
                    >
                      ×
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Person B Strategies */}
        <Card style={{ borderRadius: 0, border: '3px solid #8b5cf6' }}>
          <CardHeader style={{ backgroundColor: '#ede9fe' }}>
            <CardTitle>Person B's Ideas</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex gap-2">
              <Input
                placeholder="Add strategy..."
                value={newStrategyB}
                onChange={(e) => setNewStrategyB(e.target.value)}
                className="border-black"
                style={{ borderRadius: 0 }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newStrategyB) {
                    setPersonBStrategies([...personBStrategies, newStrategyB]);
                    setNewStrategyB('');
                  }
                }}
              />
              <Button 
                onClick={() => {
                  if (newStrategyB) {
                    setPersonBStrategies([...personBStrategies, newStrategyB]);
                    setNewStrategyB('');
                  }
                }}
                className="bg-black text-white"
                style={{ borderRadius: 0 }}
              >
                +
              </Button>
            </div>
            <div className="space-y-2">
              {personBStrategies.map((strategy, i) => (
                <div key={i} className="flex justify-between items-center p-2 bg-white border border-gray-300">
                  <span className="text-sm">{strategy}</span>
                  <button 
                    onClick={() => setPersonBStrategies(personBStrategies.filter((_, idx) => idx !== i))}
                    className="text-red-600 hover:text-red-800"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="p-6 bg-gray-50 border-l-4 border-black">
        <h4 className="font-bold mb-2">Instructions:</h4>
        <ol className="space-y-2 text-sm list-decimal list-inside">
          <li>Write the shared NEED in the center</li>
          <li>Each person adds their suggested strategies in their circle</li>
          <li>Discuss all strategies together</li>
          <li>Move mutually satisfying strategies to the MUTUAL box</li>
          <li>Be creative - look for new strategies related to but not identical to originals</li>
        </ol>
      </div>
    </div>
  );
}

// Basic Feelings & Needs Exercise - Interactive Table
export function BasicFeelingsNeedsTable({ onSave, savedData }) {
  const [entries, setEntries] = useState(savedData || {});

  const needs = [
    'Safety', 'Connection', 'Autonomy', 'Play', 'Meaning',
    'Respect', 'Love', 'Growth', 'Peace', 'Celebration',
    'Emotional Safety', 'Creativity', 'Exploration', 'Self-expression',
    'Competence', 'To Matter'
  ];

  useEffect(() => {
    onSave(entries);
  }, [entries]);

  const updateEntry = (need, field, value) => {
    setEntries({
      ...entries,
      [need]: {
        ...entries[need],
        [field]: value
      }
    });
  };

  return (
    <div className="space-y-6">
      <div className="p-6 rounded-sm" style={{ backgroundColor: 'hsl(138 25% 96%)', border: '2px solid hsl(138 30% 40%)' }}>
        <h3 className="text-2xl font-bold mb-3">Feelings/Needs Exercise (Pages 15-16)</h3>
        <div className="space-y-3 text-base text-gray-700">
          <p className="italic">
            According to Marshall Rosenberg, <strong>"The purpose of Nonviolent Communication is to create a quality of connection where everyone's needs are met through compassionate giving."</strong>
          </p>
          <p>
            Doing this well requires knowing what we are feeling from moment to moment. And also discerning the needs, met or unmet, that are giving rise to those feelings.
          </p>
          <p className="font-semibold">
            In this exercise, write down some of the feelings that occur for you when these needs are met and when they are not met.
          </p>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-2 border-black">
          <thead>
            <tr style={{ backgroundColor: 'hsl(138 20% 85%)' }}>
              <th className="border-2 border-black p-4 text-left font-bold">NEED</th>
              <th className="border-2 border-black p-4 text-left font-bold" style={{ backgroundColor: '#d1fae5' }}>
                Feelings When SATISFIED
              </th>
              <th className="border-2 border-black p-4 text-left font-bold" style={{ backgroundColor: '#fee2e2' }}>
                Feelings When NOT Satisfied
              </th>
            </tr>
          </thead>
          <tbody>
            {needs.map(need => (
              <tr key={need}>
                <td className="border-2 border-black p-3 font-semibold bg-gray-50">{need}</td>
                <td className="border-2 border-black p-2">
                  <Input
                    placeholder="e.g., happy, grateful, peaceful..."
                    value={entries[need]?.satisfied || ''}
                    onChange={(e) => updateEntry(need, 'satisfied', e.target.value)}
                    className="border-none bg-transparent"
                  />
                </td>
                <td className="border-2 border-black p-2">
                  <Input
                    placeholder="e.g., sad, frustrated, anxious..."
                    value={entries[need]?.unsatisfied || ''}
                    onChange={(e) => updateEntry(need, 'unsatisfied', e.target.value)}
                    className="border-none bg-transparent"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Compassionate Communication Checklist (Page 48)
export function CommunicationChecklist({ onSave, savedData }) {
  const [checks, setChecks] = useState(savedData || {});

  const beforeItems = [
    'Translated all jackal thoughts into self empathy?',
    'Mourned my unmet needs?',
    "Considered the other person's feelings and needs?"
  ];

  const duringItems = [
    { q: 'Pure observation or evaluation?', sub: [] },
    { q: 'Stating a feeling or a thought?', sub: [] },
    { q: 'Stating a need or a request?', sub: [] },
    { q: 'Making a request or a demand?', sub: [] },
    { q: 'Rate of talking conducive to:', sub: ['quality of connection?', 'getting my needs met?', 'caring about their needs?'] },
    { q: 'Number of words (chunking) conducive to:', sub: ['quality of connection?', 'getting my needs met?', 'caring about their needs?'] },
    { q: 'Alternating turns conducive to:', sub: ['quality of connection?', 'getting my needs met?', 'caring about their needs?'] },
    { q: 'Expressed gratitude for ability to be honest and empathetic?', sub: [] }
  ];

  useEffect(() => {
    onSave(checks);
  }, [checks]);

  return (
    <div className="space-y-6">
      <div className="p-6 rounded-sm" style={{ backgroundColor: 'hsl(138 25% 96%)', border: '2px solid hsl(138 30% 40%)' }}>
        <h3 className="text-2xl font-bold mb-3">Compassionate Communication Checklist (Page 48)</h3>
        <p className="text-base text-gray-700">
          Things to consider to maximize quality of connection, especially in conflict.
        </p>
      </div>

      {/* Before Speaking */}
      <Card style={{ borderRadius: 0 }}>
        <CardHeader style={{ backgroundColor: 'hsl(138 20% 90%)' }}>
          <CardTitle>Before Speaking with the Other Person</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 mt-4">
          {beforeItems.map((item, i) => (
            <div key={i} className="flex items-start gap-3">
              <input 
                type="checkbox"
                checked={checks[`before-${i}`] || false}
                onChange={(e) => setChecks({...checks, [`before-${i}`]: e.target.checked})}
                className="mt-1 w-5 h-5"
              />
              <label className="text-base">{item}</label>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* While Speaking */}
      <Card style={{ borderRadius: 0 }}>
        <CardHeader style={{ backgroundColor: 'hsl(138 20% 90%)' }}>
          <CardTitle>While Speaking with the Other Person</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 mt-4">
          {duringItems.map((item, i) => (
            <div key={i} className="space-y-2">
              <div className="flex items-start gap-3">
                <input 
                  type="checkbox"
                  checked={checks[`during-${i}`] || false}
                  onChange={(e) => setChecks({...checks, [`during-${i}`]: e.target.checked})}
                  className="mt-1 w-5 h-5"
                />
                <label className="text-base font-semibold">{item.q}</label>
              </div>
              {item.sub.length > 0 && (
                <div className="ml-10 space-y-2">
                  {item.sub.map((subItem, j) => (
                    <div key={j} className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">→ {subItem}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

// OFNR Practice Sheets (Pages 17-24)
export function OFNRPracticeSheets({ onSave, savedData }) {
  const [responses, setResponses] = useState(savedData || {});

  const scenarios = [
    "Why do you spend so much money on beer when we're having trouble paying rent?",
    "You should spend more time with me visiting my parents."
  ];

  useEffect(() => {
    onSave(responses);
  }, [responses]);

  return (
    <div className="space-y-8">
      {/* Page 17: NVC Statements and Questions */}
      <div className="p-8 rounded-sm bg-white border-2 border-black">
        <h3 className="text-3xl font-bold mb-6 text-center">NVC: Statements and Questions (Page 17)</h3>
        <p className="text-xl text-center mb-8 italic">Communicating in the NVC Dance of Honesty and Empathy</p>
        
        <div className="grid md:grid-cols-2 gap-8">
          {/* Expressing Honestly */}
          <div className="p-6 rounded-sm" style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)', border: '2px solid rgb(59, 130, 246)' }}>
            <h4 className="text-2xl font-bold mb-4">Expressing Honestly</h4>
            <p className="text-sm mb-4 italic">Expressing myself honestly through statements without blaming or criticizing</p>
            <div className="space-y-4 text-base">
              <div>
                <p className="font-bold">Observation (free from evaluation):</p>
                <p className="italic text-gray-700">"When I observe (see, hear, remember, imagine)..."</p>
              </div>
              <div>
                <p className="font-bold">Feeling:</p>
                <p className="italic text-gray-700">"I feel..."</p>
                <p className="text-sm text-gray-600">(My emotions or bodily sensations, not thoughts)</p>
              </div>
              <div>
                <p className="font-bold">Need:</p>
                <p className="italic text-gray-700">"Because I need/value..."</p>
                <p className="text-sm text-gray-600">(Needs distinct from strategies)</p>
              </div>
              <div>
                <p className="font-bold">Request:</p>
                <p className="italic text-gray-700">"Would you be willing to..."</p>
                <p className="text-sm text-gray-600">(Concrete actions without demanding)</p>
              </div>
            </div>
          </div>

          {/* Listening Empathetically */}
          <div className="p-6 rounded-sm" style={{ backgroundColor: 'rgba(16, 185, 129, 0.1)', border: '2px solid rgb(16, 185, 129)' }}>
            <h4 className="text-2xl font-bold mb-4">Listening Empathetically</h4>
            <p className="text-sm mb-4 italic">Empathically understanding you through asking, without hearing blame or criticism</p>
            <div className="space-y-4 text-base">
              <div>
                <p className="font-bold">Observation (free from evaluation):</p>
                <p className="italic text-gray-700">"When you observe (see, hear, remember, imagine)..."</p>
              </div>
              <div>
                <p className="font-bold">Feeling:</p>
                <p className="italic text-gray-700">"Do you feel...?"</p>
                <p className="text-sm text-gray-600">(Guessing emotions or sensations)</p>
              </div>
              <div>
                <p className="font-bold">Need:</p>
                <p className="italic text-gray-700">"Because you need/value...?"</p>
                <p className="text-sm text-gray-600">(Needs distinct from strategies)</p>
              </div>
              <div>
                <p className="font-bold">Request:</p>
                <p className="italic text-gray-700">"Would you like...?"</p>
                <p className="text-sm text-gray-600">(Sometimes unspoken when offering empathy)</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6 rounded-sm" style={{ backgroundColor: 'hsl(138 25% 96%)', border: '2px solid hsl(138 30% 40%)' }}>
        <h3 className="text-2xl font-bold mb-3">OFNR Practice Sheets (Pages 18-24)</h3>
        <p className="text-base text-gray-700 mb-3">
          Practice the Dance of Honesty and Empathy. For each scenario, express yourself honestly AND empathically understand the other.
        </p>
      </div>

      {scenarios.map((scenario, idx) => (
        <Card key={idx} style={{ borderRadius: 0, border: '2px solid black' }}>
          <CardHeader>
            <CardTitle className="text-xl">Scenario {idx + 1}: "{scenario}"</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Honesty Side */}
            <div className="p-6 rounded-sm" style={{ backgroundColor: '#dbeafe' }}>
              <h4 className="text-lg font-bold mb-4">Expressing Myself Honestly</h4>
              <div className="space-y-4">
                <div>
                  <Label>Observation (free from evaluation):</Label>
                  <Input
                    placeholder="When I observe..."
                    value={responses[`${idx}-honesty-obs`] || ''}
                    onChange={(e) => setResponses({...responses, [`${idx}-honesty-obs`]: e.target.value})}
                    className="border-black mt-1"
                    style={{ borderRadius: 0 }}
                  />
                </div>
                <div>
                  <Label>Feeling:</Label>
                  <Input
                    placeholder="I feel..."
                    value={responses[`${idx}-honesty-feel`] || ''}
                    onChange={(e) => setResponses({...responses, [`${idx}-honesty-feel`]: e.target.value})}
                    className="border-black mt-1"
                    style={{ borderRadius: 0 }}
                  />
                </div>
                <div>
                  <Label>Need:</Label>
                  <Input
                    placeholder="Because I need/value..."
                    value={responses[`${idx}-honesty-need`] || ''}
                    onChange={(e) => setResponses({...responses, [`${idx}-honesty-need`]: e.target.value})}
                    className="border-black mt-1"
                    style={{ borderRadius: 0 }}
                  />
                </div>
                <div>
                  <Label>Request:</Label>
                  <Input
                    placeholder="Would you be willing to..."
                    value={responses[`${idx}-honesty-req`] || ''}
                    onChange={(e) => setResponses({...responses, [`${idx}-honesty-req`]: e.target.value})}
                    className="border-black mt-1"
                    style={{ borderRadius: 0 }}
                  />
                </div>
              </div>
            </div>

            {/* Empathy Side */}
            <div className="p-6 rounded-sm" style={{ backgroundColor: '#d1fae5' }}>
              <h4 className="text-lg font-bold mb-4">Empathically Understanding You</h4>
              <div className="space-y-4">
                <div>
                  <Label>Observation (what you observe):</Label>
                  <Input
                    placeholder="When you observe..."
                    value={responses[`${idx}-empathy-obs`] || ''}
                    onChange={(e) => setResponses({...responses, [`${idx}-empathy-obs`]: e.target.value})}
                    className="border-black mt-1"
                    style={{ borderRadius: 0 }}
                  />
                </div>
                <div>
                  <Label>Feeling (guessing):</Label>
                  <Input
                    placeholder="Do you feel...?"
                    value={responses[`${idx}-empathy-feel`] || ''}
                    onChange={(e) => setResponses({...responses, [`${idx}-empathy-feel`]: e.target.value})}
                    className="border-black mt-1"
                    style={{ borderRadius: 0 }}
                  />
                </div>
                <div>
                  <Label>Need (guessing):</Label>
                  <Input
                    placeholder="Because you need/value...?"
                    value={responses[`${idx}-empathy-need`] || ''}
                    onChange={(e) => setResponses({...responses, [`${idx}-empathy-need`]: e.target.value})}
                    className="border-black mt-1"
                    style={{ borderRadius: 0 }}
                  />
                </div>
                <div>
                  <Label>Request (guessing):</Label>
                  <Input
                    placeholder="Would you like...?"
                    value={responses[`${idx}-empathy-req`] || ''}
                    onChange={(e) => setResponses({...responses, [`${idx}-empathy-req`]: e.target.value})}
                    className="border-black mt-1"
                    style={{ borderRadius: 0 }}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
